import { queryClient } from "@/config/react-query";

export enum AssignOp {
  ADD = "add",
  REMOVE = "remove",
}

export function editIdSet(
  currentIds: Iterable<string> | undefined,
  itemId: string,
  op: AssignOp,
): string[] {
  const set = new Set(currentIds ?? []);
  if (op === AssignOp.ADD) set.add(itemId);
  else set.delete(itemId);
  return Array.from(set);
}

export async function startOptimisticChange<TPrev>(
  key: readonly unknown[],
  apply: () => void,
): Promise<{ previous?: TPrev }> {
  await queryClient.cancelQueries({ queryKey: key });
  const previous = queryClient.getQueryData<TPrev>(key);
  apply();
  return { previous };
}

export function revertOnError<TPrev>(
  key: readonly unknown[],
  previous: TPrev | undefined,
) {
  if (typeof previous !== "undefined") {
    queryClient.setQueryData(key, previous);
  }
}

export function invalidateByKey(key: readonly unknown[]) {
  return queryClient.invalidateQueries({ queryKey: key });
}

export type QueuedMutationOptions<TKey extends string, TInput, TResult> = {
  debounceMs: number;
  getKey: (input: TInput) => TKey | null | undefined;
  mutationFn: (input: TInput) => Promise<TResult>;
  merge?: (previous: TInput, next: TInput) => TInput;
  getPayload?: (key: TKey, queuedInput: TInput) => TInput;
};

type QueuedMutationWaiter<TResult> = {
  resolve: (value: TResult) => void;
  reject: (reason: unknown) => void;
};

type QueuedMutationState<TInput, TResult> = {
  input: TInput;
  waiters: Array<QueuedMutationWaiter<TResult>>;
  timer: ReturnType<typeof setTimeout> | null;
  inFlight: boolean;
};

export class QueuedMutation<TKey extends string, TInput, TResult> {
  private readonly queueByKey = new Map<
    TKey,
    QueuedMutationState<TInput, TResult>
  >();

  constructor(
    private readonly options: QueuedMutationOptions<TKey, TInput, TResult>,
  ) {}

  enqueue(input: TInput): Promise<TResult> {
    const key = this.options.getKey(input);
    if (!key) return this.options.mutationFn(input);

    return new Promise<TResult>((resolve, reject) => {
      const queued = this.queueByKey.get(key);
      if (queued) {
        queued.input = this.merge(queued.input, input);
        queued.waiters.push({ resolve, reject });
        if (!queued.inFlight) this.schedule(key, queued);
        return;
      }

      const next: QueuedMutationState<TInput, TResult> = {
        input,
        waiters: [{ resolve, reject }],
        timer: null,
        inFlight: false,
      };
      this.queueByKey.set(key, next);
      this.schedule(key, next);
    });
  }

  private merge(previous: TInput, next: TInput): TInput {
    if (this.options.merge) return this.options.merge(previous, next);
    return next;
  }

  private schedule(
    key: TKey,
    queued: QueuedMutationState<TInput, TResult>,
    delay = this.options.debounceMs,
  ) {
    if (queued.timer) clearTimeout(queued.timer);
    queued.timer = setTimeout(() => void this.flush(key), delay);
  }

  private async flush(key: TKey) {
    const queued = this.queueByKey.get(key);
    if (!queued || queued.inFlight) return;

    queued.inFlight = true;
    if (queued.timer) clearTimeout(queued.timer);
    queued.timer = null;

    const input = queued.input;
    const waiters = queued.waiters;
    queued.waiters = [];

    try {
      const payload = this.options.getPayload?.(key, input) ?? input;
      const result = await this.options.mutationFn(payload);
      waiters.forEach(({ resolve }) => resolve(result));
    } catch (error) {
      waiters.forEach(({ reject }) => reject(error));
    } finally {
      queued.inFlight = false;
      if (queued.waiters.length > 0) {
        this.schedule(key, queued, 0);
      } else {
        this.queueByKey.delete(key);
      }
    }
  }
}
